/**
 * ใบขอซื้อแบบหัวกระดาษ + หลายรายการ (D-173) — `/v1/purchase-requests`
 *
 * คู่ขนานกับ `/v1/purchase-requisitions` (D-084) ซึ่งไม่ถูกแตะ เลขที่ `PR-<โรงงาน>-YYMMXXXX` เซิร์ฟเวอร์เป็นผู้ออก
 * (นับแยกต่อโรงงาน เริ่ม 0001 ใหม่ทุกเดือน) และแต่ละรายการมีรหัส `<เลขที่>-001`, `-002` …
 * ปริมาณเป็น string ตลอด — ไม่แปลงเป็น number
 */

import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

export const PURCHASE_REQUESTS_PATH = '/v1/purchase-requests';

export interface PurchaseRequestLine {
  readonly line_no: number;
  readonly line_code: string;
  readonly product_id: string;
  readonly product_code: string;
  readonly product_name: string;
  readonly quantity: string;
  readonly uom_code: string;
  readonly note: string | null;
  /** อยู่บนบรรทัดของใบสั่งซื้อแล้ว (D-187 A3) — เซิร์ฟเวอร์รุ่นเก่าไม่ส่ง = false */
  readonly ordered: boolean;
}

export interface PurchaseRequest {
  readonly pr_id: string;
  readonly request_no: string;
  readonly plant_code: string;
  readonly status: string;
  readonly note: string | null;
  readonly created_by_employee_id: string;
  readonly created_at: string;
  /** D-187 C3 — `null` เมื่อไม่ได้ระบุ หรือเซิร์ฟเวอร์รุ่นเก่า */
  readonly deliver_to_location_code: string | null;
  readonly lines: readonly PurchaseRequestLine[];
}

export interface CreatePurchaseRequestLine {
  readonly product_code: string;
  readonly quantity: string;
  readonly uom_code?: string;
  readonly note?: string;
}

export interface CreatePurchaseRequestBody {
  readonly plant_code: string;
  readonly note?: string;
  /** สถานที่จัดส่ง = รหัสที่เก็บของโรงงานเดียวกัน (D-187 C3) */
  readonly deliver_to_location_code?: string;
  readonly lines: readonly CreatePurchaseRequestLine[];
}

export type PurchaseRequestFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden' }
  | { readonly status: 'rejected'; readonly httpStatus: number; readonly rule: string | null; readonly message: string | null }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' };

export type PurchaseRequestResult =
  | { readonly status: 'ready'; readonly request: PurchaseRequest }
  | PurchaseRequestFailure;

export type PurchaseRequestListResult =
  | { readonly status: 'ready'; readonly items: readonly PurchaseRequest[] }
  | PurchaseRequestFailure;

export interface PurchaseRequestRequestOptions {
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  readonly fetchImpl?: typeof fetch;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

export function parsePurchaseRequestLine(raw: unknown): PurchaseRequestLine | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const r = raw as Record<string, unknown>;
  const note = r.note === null || r.note === undefined ? null : str(r.note);
  const lineCode = str(r.line_code);
  const productId = str(r.product_id);
  const productCode = str(r.product_code);
  const productName = str(r.product_name);
  const quantity = str(r.quantity);
  const uom = str(r.uom_code);
  if (
    typeof r.line_no !== 'number' ||
    lineCode === null ||
    productId === null ||
    productCode === null ||
    productName === null ||
    quantity === null ||
    uom === null
  ) {
    return null;
  }
  return {
    line_no: r.line_no,
    line_code: lineCode,
    product_id: productId,
    product_code: productCode,
    product_name: productName,
    quantity,
    uom_code: uom,
    note,
    ordered: r.ordered === true,
  };
}

export function parsePurchaseRequest(raw: unknown): PurchaseRequest | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const r = raw as Record<string, unknown>;
  const prId = str(r.pr_id);
  const requestNo = str(r.request_no);
  const plantCode = str(r.plant_code);
  const status = str(r.status);
  const createdBy = str(r.created_by_employee_id);
  const createdAt = str(r.created_at);
  if (
    prId === null ||
    requestNo === null ||
    plantCode === null ||
    status === null ||
    createdBy === null ||
    createdAt === null ||
    !Array.isArray(r.lines)
  ) {
    return null;
  }
  const lines: PurchaseRequestLine[] = [];
  for (const item of r.lines) {
    const line = parsePurchaseRequestLine(item);
    if (line === null) {
      return null;
    }
    lines.push(line);
  }
  return {
    pr_id: prId,
    request_no: requestNo,
    plant_code: plantCode,
    status,
    note: r.note === null || r.note === undefined ? null : str(r.note),
    created_by_employee_id: createdBy,
    created_at: createdAt,
    deliver_to_location_code: typeof r.deliver_to_location_code === 'string' ? r.deliver_to_location_code : null,
    lines,
  };
}

export function serializeCreatePurchaseRequestBody(body: CreatePurchaseRequestBody): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    plant_code: body.plant_code,
    lines: body.lines.map((line) => {
      const entry: Record<string, unknown> = { product_code: line.product_code, quantity: line.quantity };
      if (line.uom_code !== undefined && line.uom_code !== '') {
        entry.uom_code = line.uom_code;
      }
      if (line.note !== undefined && line.note !== '') {
        entry.note = line.note;
      }
      return entry;
    }),
  };
  if (body.note !== undefined && body.note !== '') {
    payload.note = body.note;
  }
  if (body.deliver_to_location_code !== undefined && body.deliver_to_location_code !== '') {
    payload.deliver_to_location_code = body.deliver_to_location_code;
  }
  return payload;
}

async function request(
  path: string,
  init: RequestInit,
  options: PurchaseRequestRequestOptions,
): Promise<{ readonly status: 'payload'; readonly payload: unknown } | PurchaseRequestFailure> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  const url = buildApiUrl(options.apiBaseUrl ?? getConfiguredApiBaseUrl(), path);
  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    return { status: 'network-error', detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ' };
  }
  if (response.status === 401) {
    return { status: 'unauthenticated' };
  }
  if (response.status === 403) {
    return { status: 'forbidden' };
  }
  if (response.status >= 400 && response.status < 500) {
    let rule: string | null = null;
    let message: string | null = null;
    try {
      const body = (await response.json()) as { message?: unknown; details?: { rule?: unknown } };
      message = typeof body.message === 'string' ? body.message : null;
      rule = typeof body.details?.rule === 'string' ? body.details.rule : null;
    } catch {
      /* ไม่มี body ที่อ่านได้ — แสดงเฉพาะสถานะ */
    }
    return { status: 'rejected', httpStatus: response.status, rule, message };
  }
  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }
  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/** `POST /v1/purchase-requests` — 201 พร้อมเลขที่ที่เซิร์ฟเวอร์ออกให้ (ส่ง `idempotencyKey` เพื่อกันใบซ้ำ) */
export async function createPurchaseRequest(
  body: CreatePurchaseRequestBody,
  options: PurchaseRequestRequestOptions & { readonly idempotencyKey?: string } = {},
): Promise<PurchaseRequestResult> {
  const result = await request(
    PURCHASE_REQUESTS_PATH,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        // กดซ้ำ/ส่งซ้ำหลังเน็ตหลุดด้วยคีย์เดิม = เซิร์ฟเวอร์คืนใบเดิม ไม่สร้างใบใหม่ (D-178)
        ...(options.idempotencyKey ? { 'Idempotency-Key': options.idempotencyKey } : {}),
      },
      body: JSON.stringify(serializeCreatePurchaseRequestBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const parsed = parsePurchaseRequest(result.payload);
  return parsed === null ? { status: 'invalid-response' } : { status: 'ready', request: parsed };
}

/** `GET /v1/purchase-requests` — ใหม่สุดก่อน */
export async function fetchPurchaseRequests(
  options: PurchaseRequestRequestOptions & { readonly limit?: number; readonly status?: string } = {},
): Promise<PurchaseRequestListResult> {
  const params = new URLSearchParams();
  if (options.limit !== undefined) {
    params.append('limit', String(options.limit));
  }
  if (options.status) {
    params.append('status', options.status);
  }
  const query = params.toString() === '' ? '' : `?${params.toString()}`;
  const result = await request(
    `${PURCHASE_REQUESTS_PATH}${query}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const payload = result.payload as { items?: unknown } | null;
  if (typeof payload !== 'object' || payload === null || !Array.isArray(payload.items)) {
    return { status: 'invalid-response' };
  }
  const items: PurchaseRequest[] = [];
  for (const raw of payload.items) {
    const parsed = parsePurchaseRequest(raw);
    if (parsed === null) {
      return { status: 'invalid-response' };
    }
    items.push(parsed);
  }
  return { status: 'ready', items };
}

/** `GET /v1/purchase-requests/{id}` — ใบเดียวพร้อมรายการ (ลิงก์ตรงจากแอปภายนอก) */
export async function fetchPurchaseRequest(
  prId: string,
  options: PurchaseRequestRequestOptions = {},
): Promise<PurchaseRequestResult> {
  const result = await request(
    `${PURCHASE_REQUESTS_PATH}/${encodeURIComponent(prId)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const parsed = parsePurchaseRequest(result.payload);
  return parsed === null ? { status: 'invalid-response' } : { status: 'ready', request: parsed };
}

/** `POST /v1/purchase-requests/{id}/approve|cancel` — Manager (APPROVE); ผู้สร้างอนุมัติ/ยกเลิกใบของตัวเองได้ (D-185) */
export async function transitionPurchaseRequest(
  prId: string,
  action: 'approve' | 'cancel',
  options: PurchaseRequestRequestOptions = {},
): Promise<PurchaseRequestResult> {
  const result = await request(
    `${PURCHASE_REQUESTS_PATH}/${encodeURIComponent(prId)}/${action}`,
    { method: 'POST', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const parsed = parsePurchaseRequest(result.payload);
  return parsed === null ? { status: 'invalid-response' } : { status: 'ready', request: parsed };
}
